import { html, nothing, type TemplateResult } from "lit";
import type {
  WorkforceAgentRow,
  WorkforceBindingRow,
  WorkforceConfig,
  WorkforceInsightsState,
  WorkforceRoleRow,
  WorkforceSimulateResultState,
  WorkforceUnitRow,
} from "../app-view-state.ts";
import type { ConnectorInfo, EmployeeRow, SyncHistoryRow } from "./employees.ts";
import { formatAgo } from "../format.ts";

export type WorkforceView =
  | "overview"
  | "agents"
  | "roles"
  | "units"
  | "bindings"
  | "routing"
  | "analytics"
  | "fleet";

export type WorkforceUiProps = {
  loading: boolean;
  saving: boolean;
  error: string | null;
  config: WorkforceConfig | null;
  agents: WorkforceAgentRow[];
  roles: WorkforceRoleRow[];
  units: WorkforceUnitRow[];
  bindings: WorkforceBindingRow[];
  insights: WorkforceInsightsState | null;
  simulateBusy: boolean;
  simulateError: string | null;
  simulateResult: WorkforceSimulateResultState | null;
  employees: EmployeeRow[];
  connectors: ConnectorInfo[];
  syncHistory: SyncHistoryRow[];
  employeesStatus: {
    enabled: boolean;
    connectorConfigured: boolean;
    activeConnector?: string;
    employeeCount: number;
  } | null;
  selectedView: WorkforceView;
  selectedAgentId: string | null;
  onRefresh: () => void;
  onSelectView: (view: WorkforceView) => void;
  onSelectAgent: (agentId: string | null) => void;
  onEmployeesRefresh: () => void;
  onEmployeesDryRunSync: () => void;
  onEmployeesSync: () => void;
  onEmployeesTestConnection: () => void;
  onOpenEmployees: () => void;
  onOpenConfig: () => void;
  onRequestNotificationPermission: () => void;
  onSimulateRoute: (input: {
    channelId: string;
    messageType: "dm" | "group" | "channel" | "webhook";
    content?: string;
    requiredSkills?: string[];
  }) => void;
  onAssignAgentRoleUnit: (agentId: string, roleId: string, unitId: string) => void;
  onAddRole: (payload: {
    roleId: string;
    name: string;
    description?: string;
    skills: string[];
    priority?: number;
    maxSessions?: number;
    /**
     * The model this role runs on.
     *
     * JAISIU-4515 §4 — models are assigned to roles, never to an
     * individual agent.
     */
    model?: string;
  }) => void;
  onAddUnit: (payload: {
    unitId: string;
    name: string;
    parentUnitId: string | null;
    supervisorId: string;
    type: "division" | "department" | "team" | "project";
    tags?: string[];
  }) => void;
  onAddBinding: (payload: {
    bindingId: string;
    agentId: string;
    channels?: string[];
    keywords?: string[];
    requiredSkills?: string[];
    units?: string[];
    roles?: string[];
    messageTypes?: string[];
    priority: number;
    fallback: boolean;
  }) => void;
  fleetLoading: boolean;
  fleetError: string | null;
  fleetLoads: Array<{ agentId: string; activeSessions: number; isOverloaded: boolean }>;
  fleetActiveAgents: Array<{
    agentId: string;
    sessionKey: string;
    label?: string;
    status: string;
    message?: string;
    lastUpdate: number;
    spawnedAt: number;
  }>;
  fleetStats: {
    totalActiveSessions: number;
    uniqueAgents: number;
    overloadedAgents: number;
    eventHistorySize: number;
  } | null;
  fleetEventHistory: Array<{
    type: "agent:spawned" | "agent:status" | "agent:completed" | "agent:error";
    agentId: string;
    sessionKey: string;
    label?: string;
    status?: string;
    message?: string;
    error?: string;
    timestamp: number;
  }>;
  onRefreshFleet: () => void;
};

type CriticalEmployeeRow = {
  employeeId: string;
  name: string;
  role: string;
  unit: string;
  score: number;
  reasons: string[];
};

const VIEWS: WorkforceView[] = [
  "overview",
  "routing",
  "analytics",
  "agents",
  "roles",
  "units",
  "bindings",
  "fleet",
];

export function renderWorkforceUi(props: WorkforceUiProps) {
  const critical = computeCriticalEmployees(props);
  const selectedAgent =
    props.agents.find((agent) => agent.agentId === props.selectedAgentId) ??
    props.agents[0] ??
    null;

  const activeAgents = props.agents.filter((agent) => agent.status === "active").length;
  const roleCoverage = new Set(
    props.agents.map((agent) => agent.roleId ?? agent.role).filter(Boolean),
  ).size;
  const unitCoverage = new Set(props.agents.map((agent) => agent.unitId).filter(Boolean)).size;
  const replaceable = props.agents.filter((agent) => (agent.skills?.length ?? 0) > 1).length;
  const replacementReadyPct =
    props.agents.length > 0 ? Math.round((replaceable / props.agents.length) * 100) : 0;

  return html`
    <section class="workforce-view">
      <div class="row" style="justify-content: space-between; align-items: flex-start; margin-bottom: 20px;">
        <div>
          <h1 style="margin: 0; font-size: 24px; font-weight: 700;">Prism Workflow Automation</h1>
          <p class="muted" style="margin: 6px 0 0 0;">
            Build employee-aware automation flows: ATS -> hierarchy -> routing -> fallback agents.
          </p>
        </div>
        <div class="row" style="gap: 10px; align-items: center;">
          ${
            props.saving
              ? html`
                  <span class="chip-warn" style="font-size: 11px">Saving...</span>
                `
              : nothing
          }
          <button class="btn" ?disabled=${props.loading || props.saving} @click=${props.onEmployeesRefresh}>
            Refresh Employees
          </button>
          <button class="btn" ?disabled=${props.loading || props.saving} @click=${props.onRefresh}>
            ${props.loading ? "Loading..." : "Refresh workflow"}
          </button>
          <button class="btn primary" @click=${props.onOpenEmployees}>Open Employees</button>
        </div>
      </div>

      ${
        props.error
          ? html`<div class="callout danger" style="margin-bottom: 16px;">${props.error}</div>`
          : nothing
      }

      ${renderBudgetAlertBanner(props)}

      ${renderAtsSection(props)}

      <div style="display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 10px; margin-bottom: 16px;">
        <div class="stat"><div class="stat-label">Agents</div><div class="stat-value">${props.agents.length}</div></div>
        <div class="stat"><div class="stat-label">Active</div><div class="stat-value">${activeAgents}</div></div>
        <div class="stat"><div class="stat-label">Role Coverage</div><div class="stat-value">${roleCoverage}</div></div>
        <div class="stat"><div class="stat-label">Unit Coverage</div><div class="stat-value">${unitCoverage}</div></div>
        <div class="stat"><div class="stat-label">Replaceability</div><div class="stat-value">${replacementReadyPct}%</div></div>
        <div class="stat"><div class="stat-label">Critical Employees</div><div class="stat-value">${critical.length}</div></div>
      </div>

      <div class="row" style="gap: 8px; margin-bottom: 14px; flex-wrap: wrap;">
        ${VIEWS.map(
          (view) => html`
            <button class="btn ${props.selectedView === view ? "primary" : ""}" @click=${() => props.onSelectView(view)}>
              ${labelForView(view)}
            </button>
          `,
        )}
      </div>

      ${props.selectedView === "overview" ? renderOverviewPanel(props, critical) : nothing}
      ${props.selectedView === "routing" ? renderRoutingPanel(props, selectedAgent) : nothing}
      ${props.selectedView === "analytics" ? renderAnalyticsPanel(props) : nothing}
      ${props.selectedView === "agents" ? renderAgentsPanel(props, critical) : nothing}
      ${props.selectedView === "roles" ? renderRolesPanel(props) : nothing}
      ${props.selectedView === "units" ? renderUnitsPanel(props) : nothing}
      ${props.selectedView === "bindings" ? renderBindingsPanel(props) : nothing}
      ${props.selectedView === "fleet" ? renderFleetPanel(props) : nothing}
    </section>
  `;
}

function renderAtsSection(props: WorkforceUiProps) {
  const connectors = props.connectors ?? [];
  const history = props.syncHistory ?? [];
  const status = props.employeesStatus;

  return html`
    <div class="card" style="margin-bottom: 16px;">
      <div class="row" style="justify-content: space-between; align-items: center; margin-bottom: 10px;">
        <div>
          <div style="font-size: 14px; font-weight: 700;">ATS / HR Ingestion</div>
          <div class="muted" style="font-size: 12px;">
            Import org structure and employees from connectors, then map routing to roles and units.
          </div>
        </div>
        <div class="row" style="gap: 8px;">
          <button class="btn" @click=${props.onEmployeesTestConnection}>Test Connection</button>
          <button class="btn" @click=${props.onEmployeesDryRunSync}>Dry Run</button>
          <button class="btn primary" @click=${props.onEmployeesSync}>Sync Now</button>
          <button class="btn" @click=${props.onOpenConfig}>Open Config</button>
        </div>
      </div>

      <div style="display: grid; grid-template-columns: 1.5fr 1fr; gap: 14px;">
        <div style="border: 1px solid var(--border); border-radius: 8px; padding: 10px;">
          <div style="font-size: 12px; font-weight: 700; margin-bottom: 8px;">Connector Inventory</div>
          ${
            connectors.length === 0
              ? html`
                  <div class="muted" style="font-size: 13px">No ATS connectors available.</div>
                `
              : html`${connectors.map(
                  (connector) => html`
                    <div style="display: flex; justify-content: space-between; align-items: center; border-top: 1px solid var(--border); padding: 8px 0;">
                      <div>
                        <div style="font-size: 13px; font-weight: 600;">${connector.name}</div>
                        <div class="muted" style="font-size: 12px;">${connector.id} (v${connector.version})</div>
                      </div>
                      ${
                        status?.activeConnector === connector.id
                          ? html`
                              <span class="chip-ok" style="font-size: 11px">Active</span>
                            `
                          : html`
                              <span class="chip" style="font-size: 11px">Available</span>
                            `
                      }
                    </div>
                  `,
                )}`
          }
        </div>

        <div style="border: 1px solid var(--border); border-radius: 8px; padding: 10px;">
          <div style="font-size: 12px; font-weight: 700; margin-bottom: 8px;">Latest Syncs</div>
          ${
            history.length === 0
              ? html`
                  <div class="muted" style="font-size: 13px">No sync runs yet.</div>
                `
              : html`${history.slice(0, 3).map(
                  (run) => html`
                    <div style="border-top: 1px solid var(--border); padding: 8px 0;">
                      <div style="display: flex; justify-content: space-between;">
                        <span style="font-size: 12px; font-weight: 600;">${run.connector}</span>
                        <span class="muted" style="font-size: 11px;">${formatAgo(new Date(run.timestamp).getTime())}</span>
                      </div>
                      <div class="muted" style="font-size: 11px;">
                        +${run.employeesAdded} ~${run.employeesUpdated} -${run.employeesRemoved} / errors ${run.errorsCount}
                      </div>
                    </div>
                  `,
                )}`
          }
        </div>
      </div>
    </div>
  `;
}

function renderOverviewPanel(props: WorkforceUiProps, critical: CriticalEmployeeRow[]) {
  return html`
    <div style="display: grid; grid-template-columns: 1.25fr 1fr; gap: 14px;">
      <div class="card">
        <div class="card-title">Company Structure Snapshot</div>
        <div style="display: grid; gap: 8px;">
          <div class="muted" style="font-size: 12px;">Workflow enabled: <strong>${props.config?.enabled ? "Yes" : "No"}</strong></div>
          <div class="muted" style="font-size: 12px;">Default strategy: <strong>${props.config?.defaultStrategy?.kind ?? "not-set"}</strong></div>
          <div class="muted" style="font-size: 12px;">Escalation depth: <strong>${props.config?.escalation?.maxDepth ?? 3}</strong></div>
          <div style="margin-top: 8px;">${renderHierarchyTree(props.units, props.agents)}</div>
        </div>
      </div>
      <div class="card">
        <div class="card-title">Critical Employees (Top 6)</div>
        ${
          critical.length === 0
            ? html`
                <div class="muted">No critical employees identified yet.</div>
              `
            : html`<div style="display: grid; gap: 8px;">${critical
                .slice(0, 6)
                .map(
                  (entry) =>
                    html`<div style="border: 1px solid var(--border); border-radius: 8px; padding: 8px;"><div style="display: flex; justify-content: space-between;"><strong style="font-size: 13px;">${entry.name}</strong><span class="chip-warn" style="font-size: 11px;">Score ${entry.score}</span></div><div class="muted" style="font-size: 11px;">${entry.role} -> ${entry.unit} -> ${entry.reasons.join(", ")}</div></div>`,
                )}</div>`
        }
      </div>
    </div>
  `;
}

function renderBudgetAlertBanner(props: WorkforceUiProps) {
  const alerts = props.insights?.budgetAlerts ?? [];
  if (alerts.length === 0) {
    return nothing;
  }
  return html`
    <div class="callout danger" style="margin-bottom: 16px;">
      <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap;">
        <div>
          <strong>Budget alerts</strong>
          <span class="muted" style="font-size: 12px; display: block; margin-top: 4px;">
            ${alerts.length} agent(s) at or above ${props.insights?.budget.alertThreshold ?? 80}% of cap
            (workflow budget monitoring).
          </span>
          <ul style="margin: 8px 0 0 18px; padding: 0; font-size: 13px;">
            ${alerts
              .slice(0, 6)
              .map(
                (a) =>
                  html`<li><strong>${a.name}</strong> — ${a.percentUsed}% (${a.currentSpend} / ${a.maxBudget})</li>`,
              )}
          </ul>
        </div>
        <button type="button" class="btn btn--sm" @click=${() => props.onRequestNotificationPermission()}>
          Enable desktop alerts
        </button>
      </div>
    </div>
  `;
}

function renderRoutingPanel(props: WorkforceUiProps, selectedAgent: WorkforceAgentRow | null) {
  const topBinding = [...props.bindings].sort((a, b) => b.priority - a.priority)[0] ?? null;
  const sim = props.simulateResult;

  return html`
    <div style="display: grid; gap: 14px;">
      <div class="card">
        <div class="card-title">Automation Workflow Canvas</div>
        <div class="muted" style="margin-bottom: 12px;">
          n8n-like pipeline for employee routing and ATS-fed org context.
        </div>
        <div style="display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 10px;">
          ${renderWorkflowNode("Trigger", "ATS sync / inbound message / channel event")}
          ${renderWorkflowNode(
            "Classifier",
            topBinding
              ? `Match: ${formatArray(topBinding.match.requiredSkills)} / ${formatArray(topBinding.match.keywords)}`
              : "Infer skills from content and channel metadata",
          )}
          ${renderWorkflowNode(
            "Org Gate",
            topBinding
              ? `Units ${formatArray(topBinding.match.units)} | Roles ${formatArray(topBinding.match.roles)}`
              : "Apply hierarchy constraints (unit + role)",
          )}
          ${renderWorkflowNode(
            "Assignee",
            selectedAgent
              ? `${selectedAgent.name} (${selectedAgent.agentId})`
              : "Select best active routing agent",
          )}
          ${renderWorkflowNode(
            "Fallback",
            topBinding?.fallback ? "Fallback branch active" : "Escalate to manager / default agent",
          )}
        </div>
        <div style="display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap;">
          ${props.bindings.slice(0, 8).map(
            (binding) =>
              html`<button class="chip" style="cursor: pointer;" @click=${() => props.onSelectAgent(binding.agentId)}>
                ${binding.bindingId} -&gt; ${binding.agentId}
              </button>`,
          )}
        </div>
      </div>

      <div class="card" data-route-sim>
        <div class="card-title">Routing simulator</div>
        <div class="muted" style="margin-bottom: 10px; font-size: 12px;">
          Runs the same path as CLI <code>workforce route</code> against the live gateway config (channel access,
          bindings, skills).
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr 2fr; gap: 10px; align-items: end;">
          <label style="display: grid; gap: 4px; font-size: 12px;">
            Channel id
            <input data-sim-channel placeholder="telegram" value="telegram" />
          </label>
          <label style="display: grid; gap: 4px; font-size: 12px;">
            Message type
            <select data-sim-type>
              <option value="dm" selected>dm</option>
              <option value="group">group</option>
              <option value="channel">channel</option>
              <option value="webhook">webhook</option>
            </select>
          </label>
          <label style="display: grid; gap: 4px; font-size: 12px;">
            Required skills (comma)
            <input data-sim-skills placeholder="sales, api" />
          </label>
        </div>
        <label style="display: grid; gap: 4px; font-size: 12px; margin-top: 10px;">
          Message content (optional)
          <textarea data-sim-content rows="3" style="resize: vertical;" placeholder="Customer question text..."></textarea>
        </label>
        <div class="row" style="gap: 8px; margin-top: 12px; align-items: center;">
          <button
            class="btn primary"
            ?disabled=${props.simulateBusy || props.loading || props.saving}
            @click=${(ev: Event) => {
              const root = (ev.currentTarget as HTMLElement).closest("[data-route-sim]");
              const channelId = getInput(root, "[data-sim-channel]") || "telegram";
              const messageType = getInput(root, "[data-sim-type]") as
                | "dm"
                | "group"
                | "channel"
                | "webhook";
              const content = getInput(root, "[data-sim-content]");
              const skillsRaw = splitCsv(getInput(root, "[data-sim-skills]"));
              props.onSimulateRoute({
                channelId,
                messageType,
                ...(content ? { content } : {}),
                ...(skillsRaw.length ? { requiredSkills: skillsRaw } : {}),
              });
            }}
          >
            ${props.simulateBusy ? "Simulating…" : "Simulate route"}
          </button>
          ${
            props.simulateError
              ? html`<span class="muted" style="font-size: 12px; color: var(--danger);">${props.simulateError}</span>`
              : nothing
          }
        </div>
        ${
          sim
            ? html`<div
                style="margin-top: 14px; border: 1px solid var(--border); border-radius: 8px; padding: 10px;"
              >
                <div style="font-size: 12px; font-weight: 700; margin-bottom: 6px;">Result</div>
                ${
                  sim.success
                    ? html`<div style="font-size: 13px;">
                        Routed to <strong>${sim.agentId ?? "?"}</strong>
                        ${
                          sim.decision?.confidence != null
                            ? html`<span class="muted"> — confidence ${(sim.decision.confidence * 100).toFixed(1)}%</span>`
                            : nothing
                        }
                        ${
                          sim.decision?.reason
                            ? html`<div class="muted" style="margin-top: 6px; font-size: 12px;">${sim.decision.reason}</div>`
                            : nothing
                        }
                        ${
                          sim.alternatives?.length
                            ? html`<div class="muted" style="margin-top: 6px; font-size: 12px;">
                              Alternatives: ${sim.alternatives.join(", ")}
                            </div>`
                            : nothing
                        }
                      </div>`
                    : html`<div style="font-size: 13px; color: var(--danger);">
                        ${sim.denied?.reason ?? "Routing failed"}
                      </div>`
                }
              </div>`
            : nothing
        }
      </div>

      <div class="card">
        <div class="card-title">Delegation (escalation chain)</div>
        <div class="muted" style="font-size: 12px; margin-bottom: 10px;">
          Manager and unit supervisor targets computed by the routing engine for each agent.
        </div>
        ${
          !props.insights?.delegations?.length
            ? html`
                <div class="muted">Load workflow data or add agents to see delegation paths.</div>
              `
            : html`<table style="width: 100%; border-collapse: collapse;">
                <thead>
                  <tr style="text-align: left; font-size: 12px; color: var(--muted);">
                    <th style="padding: 8px;">Agent</th>
                    <th style="padding: 8px;">Chain</th>
                  </tr>
                </thead>
                <tbody>
                  ${props.insights.delegations.map(
                    (row) => html`<tr style="border-top: 1px solid var(--border);">
                      <td style="padding: 8px;">
                        <button type="button" class="btn btn--sm" @click=${() => props.onSelectAgent(row.agentId)}>
                          ${row.name}
                        </button>
                        <div class="muted" style="font-size: 11px;">${row.agentId}</div>
                      </td>
                      <td style="padding: 8px; font-size: 12px;">${row.chain.join(" → ") || "—"}</td>
                    </tr>`,
                  )}
                </tbody>
              </table>`
        }
      </div>
    </div>
  `;
}

function renderAnalyticsPanel(props: WorkforceUiProps) {
  const ins = props.insights;
  if (!ins) {
    return html`
      <div class="card">
        <div class="muted">
          Loading analytics requires a successful workforce.insights call. Refresh workflow.
        </div>
      </div>
    `;
  }

  const roleEntries = Object.entries(ins.roleDistribution).sort((a, b) => b[1] - a[1]);
  const unitEntries = Object.entries(ins.unitDistribution).sort((a, b) => b[1] - a[1]);

  return html`
    <div style="display: grid; gap: 14px;">
      <div style="display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px;">
        <div class="stat">
          <div class="stat-label">Engine agents</div>
          <div class="stat-value">${ins.stats.totalAgents}</div>
        </div>
        <div class="stat">
          <div class="stat-label">Active</div>
          <div class="stat-value">${ins.stats.activeAgents}</div>
        </div>
        <div class="stat">
          <div class="stat-label">Roles / units</div>
          <div class="stat-value">${ins.stats.totalRoles} / ${ins.stats.totalUnits}</div>
        </div>
        <div class="stat">
          <div class="stat-label">Bindings</div>
          <div class="stat-value">${ins.bindingsCount}</div>
        </div>
      </div>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 14px;">
        <div class="card">
          <div class="card-title">Agents per role</div>
          ${
            roleEntries.length === 0
              ? html`
                  <div class="muted">No distribution.</div>
                `
              : html`<table style="width: 100%; border-collapse: collapse;">
                  ${roleEntries.map(
                    ([role, count]) => html`<tr style="border-top: 1px solid var(--border);">
                      <td style="padding: 8px;">${role}</td>
                      <td style="padding: 8px; text-align: right;">${count}</td>
                    </tr>`,
                  )}
                </table>`
          }
        </div>
        <div class="card">
          <div class="card-title">Agents per unit</div>
          ${
            unitEntries.length === 0
              ? html`
                  <div class="muted">No distribution.</div>
                `
              : html`<table style="width: 100%; border-collapse: collapse;">
                  ${unitEntries.map(
                    ([unit, count]) => html`<tr style="border-top: 1px solid var(--border);">
                      <td style="padding: 8px;">${unit}</td>
                      <td style="padding: 8px; text-align: right;">${count}</td>
                    </tr>`,
                  )}
                </table>`
          }
        </div>
      </div>
      <div class="card">
        <div class="card-title">Budget utilization</div>
        <div class="muted" style="font-size: 12px; margin-bottom: 8px;">
          Budget tracking: ${ins.budget.enabled ? "on" : "off"} · Alert threshold:
          ${ins.budget.alertThreshold}%
        </div>
        ${
          ins.budgetOverview.length === 0
            ? html`
                <div class="muted">No per-agent budgets configured or budget feature disabled.</div>
              `
            : html`<table style="width: 100%; border-collapse: collapse;">
                <thead>
                  <tr style="text-align: left; font-size: 12px; color: var(--muted);">
                    <th style="padding: 8px;">Agent</th>
                    <th style="padding: 8px;">Spend / cap</th>
                    <th style="padding: 8px;">%</th>
                  </tr>
                </thead>
                <tbody>
                  ${ins.budgetOverview.map(
                    (row) => html`<tr style="border-top: 1px solid var(--border);">
                      <td style="padding: 8px;">${row.name}</td>
                      <td style="padding: 8px;">${row.currentSpend} / ${row.maxBudget}</td>
                      <td style="padding: 8px;">
                        ${row.percentUsed}
                        ${
                          row.atAlertThreshold
                            ? html`
                                <span class="chip-warn" style="font-size: 10px">alert</span>
                              `
                            : nothing
                        }
                      </td>
                    </tr>`,
                  )}
                </tbody>
              </table>`
        }
      </div>
    </div>
  `;
}

function renderFleetPanel(props: WorkforceUiProps) {
  const loads = props.fleetLoads ?? [];
  const agents = props.fleetActiveAgents ?? [];
  const stats = props.fleetStats;
  const events = props.fleetEventHistory ?? [];

  return html`
    <div class="fleet-viewport">
      <div class="fleet-header">
        <div class="fleet-header-left">
          <h3 style="margin: 0; font-size: 18px; font-weight: 600;">Fleet Control</h3>
          <span class="fleet-live-indicator ${props.fleetLoading ? "loading" : ""}">
            ${props.fleetLoading ? "Connecting..." : "Live"}
          </span>
        </div>
        <button class="btn btn-secondary" @click=${props.onRefreshFleet} ?disabled=${props.fleetLoading}>
          ${props.fleetLoading ? "Loading..." : "Refresh"}
        </button>
      </div>

      ${props.fleetError ? html`<div class="fleet-error-banner">${props.fleetError}</div>` : nothing}

      ${
        stats
          ? html`
        <div class="fleet-stats-row">
          <div class="fleet-stat-card ${stats.totalActiveSessions > 0 ? "active" : ""}">
            <div class="fleet-stat-icon">🚀</div>
            <div class="fleet-stat-content">
              <div class="fleet-stat-value">${stats.totalActiveSessions}</div>
              <div class="fleet-stat-label">Active Sessions</div>
            </div>
          </div>
          <div class="fleet-stat-card">
            <div class="fleet-stat-icon">👥</div>
            <div class="fleet-stat-content">
              <div class="fleet-stat-value">${stats.uniqueAgents}</div>
              <div class="fleet-stat-label">Unique Agents</div>
            </div>
          </div>
          <div class="fleet-stat-card ${stats.overloadedAgents > 0 ? "alert" : ""}">
            <div class="fleet-stat-icon">⚠️</div>
            <div class="fleet-stat-content">
              <div class="fleet-stat-value">${stats.overloadedAgents}</div>
              <div class="fleet-stat-label">Overloaded</div>
            </div>
          </div>
          <div class="fleet-stat-card muted">
            <div class="fleet-stat-icon">📊</div>
            <div class="fleet-stat-content">
              <div class="fleet-stat-value">${stats.eventHistorySize}</div>
              <div class="fleet-stat-label">Events Logged</div>
            </div>
          </div>
        </div>
      `
          : nothing
      }

      <div class="fleet-grid">
        <div class="fleet-panel">
          <div class="fleet-panel-header">
            <span>Agent Loads</span>
            <span class="fleet-count-badge">${loads.length}</span>
          </div>
          <div class="fleet-panel-body">
            ${
              loads.length === 0
                ? html`
                    <div class="fleet-empty-state">
                      <span class="fleet-empty-icon">📭</span>
                      <span>No fleet load data</span>
                    </div>
                  `
                : html`
              <div class="fleet-load-grid">
                ${loads.map(
                  (load) => html`
                  <div class="fleet-load-card ${load.isOverloaded ? "overloaded" : load.activeSessions > 2 ? "busy" : ""}">
                    <div class="fleet-load-header">
                      <span class="fleet-load-name">${load.agentId}</span>
                      <span class="fleet-load-badge ${load.isOverloaded ? "badge-error" : load.activeSessions > 2 ? "badge-warn" : "badge-ok"}">
                        ${load.activeSessions}
                      </span>
                    </div>
                    ${
                      load.isOverloaded
                        ? html`
                            <div class="fleet-load-alert">⚠️ Overloaded</div>
                          `
                        : nothing
                    }
                    <div class="fleet-load-bar">
                      <div class="fleet-load-fill" style="width: ${Math.min(100, (load.activeSessions / 5) * 100)}%"></div>
                    </div>
                  </div>
                `,
                )}
              </div>
            `
            }
          </div>
        </div>

        <div class="fleet-panel">
          <div class="fleet-panel-header">
            <span>Active Agents</span>
            <span class="fleet-count-badge">${agents.length}</span>
          </div>
          <div class="fleet-panel-body">
            ${
              agents.length === 0
                ? html`
                    <div class="fleet-empty-state">
                      <span class="fleet-empty-icon">🤖</span>
                      <span>No active agents</span>
                    </div>
                  `
                : html`
              <div class="fleet-agents-list">
                ${agents.map((agent) => {
                  const age = Math.round((Date.now() - agent.spawnedAt) / 1000);
                  const ageStr =
                    age < 60
                      ? `${age}s`
                      : age < 3600
                        ? `${Math.round(age / 60)}m`
                        : `${Math.round(age / 3600)}h`;
                  return html`
                    <div class="fleet-agent-row">
                      <div class="fleet-agent-status-dot ${agent.status}"></div>
                      <div class="fleet-agent-info">
                        <div class="fleet-agent-name">${agent.agentId}</div>
                        ${agent.label ? html`<div class="fleet-agent-label">${agent.label}</div>` : nothing}
                      </div>
                      <div class="fleet-agent-meta">
                        <span class="fleet-agent-status">${agent.status}</span>
                        <span class="fleet-agent-age">${ageStr}</span>
                      </div>
                    </div>
                  `;
                })}
              </div>
            `
            }
          </div>
        </div>

        <div class="fleet-panel fleet-panel-wide">
          <div class="fleet-panel-header">
            <span>Event Stream</span>
            <span class="fleet-count-badge">${events.length}</span>
          </div>
          <div class="fleet-panel-body fleet-event-body">
            ${
              events.length === 0
                ? html`
                    <div class="fleet-empty-state">
                      <span class="fleet-empty-icon">📡</span>
                      <span>No events yet</span>
                    </div>
                  `
                : html`
              <div class="fleet-events-list">
                ${events.slice(0, 30).map(
                  (evt) => html`
                  <div class="fleet-event-row ${evt.type}">
                    <span class="fleet-event-type">${formatEventType(evt.type)}</span>
                    <span class="fleet-event-agent">${evt.agentId}</span>
                    <span class="fleet-event-time">${formatEventTime(evt.timestamp)}</span>
                    ${evt.message ? html`<span class="fleet-event-msg">${evt.message}</span>` : nothing}
                  </div>
                `,
                )}
              </div>
            `
            }
          </div>
        </div>
      </div>
    </div>
  `;
}

function formatEventType(type: string): string {
  return type.replace("agent:", "");
}

function formatEventTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  if (diff < 60000) return `${Math.round(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.round(diff / 60000)}m ago`;
  return `${Math.round(diff / 3600000)}h ago`;
}

function renderWorkflowNode(title: string, body: string) {
  return html`<div style="border: 1px solid var(--border); border-radius: 10px; padding: 10px; background: var(--secondary); min-height: 82px;"><div style="font-size: 11px; text-transform: uppercase; color: var(--muted); font-weight: 700;">${title}</div><div style="margin-top: 8px; font-size: 12px; line-height: 1.35;">${body}</div></div>`;
}

function renderAgentsPanel(props: WorkforceUiProps, critical: CriticalEmployeeRow[]) {
  const criticalByEmployeeId = new Map(critical.map((row) => [row.employeeId, row]));
  return html`
    <div class="card" style="padding: 0; overflow: hidden;">
      <div style="padding: 12px 14px; border-bottom: 1px solid var(--border); font-weight: 700;">Workflow agents</div>
      <table style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr style="text-align: left; font-size: 12px; color: var(--muted);">
            <th style="padding: 10px 12px;">Agent</th>
            <th style="padding: 10px 12px;">Role / Unit</th>
            <th style="padding: 10px 12px;">Status</th>
            <th style="padding: 10px 12px;">Skills</th>
            <th style="padding: 10px 12px;">Criticality</th>
            <th style="padding: 10px 12px;">Actions</th>
          </tr>
        </thead>
        <tbody>
          ${props.agents.map((agent) => {
            const criticalRow = criticalByEmployeeId.get(agent.employeeId);
            const active = agent.status === "active" || agent.status === "onboarding";
            return html`
              <tr style="border-top: 1px solid var(--border);" data-agent-row=${agent.agentId}>
                <td style="padding: 10px 12px; cursor: pointer;" @click=${() => props.onSelectAgent(agent.agentId)}>
                  <div style="font-weight: 600;">${agent.name}</div>
                  <div class="muted" style="font-size: 12px;">${agent.agentId} / ${agent.employeeId}</div>
                </td>
                <td style="padding: 10px 12px;">
                  <div style="display: grid; gap: 4px;">
                    <select data-role-id .value=${agent.roleId ?? ""}>
                      <option value="">Unassigned role</option>
                      ${props.roles.map((role) => html`<option value=${role.roleId}>${role.name}</option>`)}
                    </select>
                    <select data-unit-id .value=${agent.unitId ?? ""}>
                      <option value="">Unassigned unit</option>
                      ${props.units.map((unit) => html`<option value=${unit.unitId}>${unit.name}</option>`)}
                    </select>
                  </div>
                </td>
                <td style="padding: 10px 12px;"><span class="${active ? "chip-ok" : "chip"}" style="font-size: 11px;">${agent.status}</span></td>
                <td style="padding: 10px 12px;" class="muted">${formatArray(agent.skills)}</td>
                <td style="padding: 10px 12px;">${
                  criticalRow
                    ? html`<span class="chip-warn" style="font-size: 11px;">${criticalRow.score}</span>`
                    : html`
                        <span class="chip-ok" style="font-size: 11px">replaceable</span>
                      `
                }</td>
                <td style="padding: 10px 12px;">
                  <button
                    class="btn btn--sm"
                    ?disabled=${props.saving}
                    @click=${(ev: Event) => {
                      const row = (ev.currentTarget as HTMLElement).closest("tr[data-agent-row]");
                      const roleId =
                        (row?.querySelector("select[data-role-id]") as HTMLSelectElement | null)
                          ?.value ?? "";
                      const unitId =
                        (row?.querySelector("select[data-unit-id]") as HTMLSelectElement | null)
                          ?.value ?? "";
                      props.onAssignAgentRoleUnit(agent.agentId, roleId, unitId);
                    }}
                  >
                    Save
                  </button>
                </td>
              </tr>
            `;
          })}
        </tbody>
      </table>
    </div>
  `;
}

function renderRolesPanel(props: WorkforceUiProps) {
  return html`
    <div class="card" style="margin-bottom: 10px;" data-role-form>
      <div class="card-title">Add Role</div>
      <div style="display: grid; grid-template-columns: 1.2fr 1.2fr 1fr 1.4fr 0.8fr 0.8fr auto; gap: 8px;">
        <input data-role-id placeholder="roleId" />
        <input data-role-name placeholder="name" />
        <input data-role-skills placeholder="skills (comma)" />
        <input data-role-model placeholder="model (provider/model)" />
        <input data-role-priority type="number" placeholder="priority" />
        <input data-role-max type="number" placeholder="maxSessions" />
        <button class="btn primary" ?disabled=${props.saving} @click=${(ev: Event) => {
          const root = (ev.currentTarget as HTMLElement).closest("[data-role-form]");
          const roleId = getInput(root, "[data-role-id]");
          const name = getInput(root, "[data-role-name]");
          const skills = splitCsv(getInput(root, "[data-role-skills]"));
          const model = getInput(root, "[data-role-model]");
          const priority = parseOptionalNumber(getInput(root, "[data-role-priority]"));
          const maxSessions = parseOptionalNumber(getInput(root, "[data-role-max]"));
          if (!roleId || !name || skills.length === 0) return;
          props.onAddRole({
            roleId,
            name,
            skills,
            ...(model ? { model } : {}),
            ...(priority != null ? { priority } : {}),
            ...(maxSessions != null ? { maxSessions } : {}),
          });
        }}>Add</button>
      </div>
    </div>
    <div class="card" style="padding: 0; overflow: hidden;"><div style="padding: 12px 14px; border-bottom: 1px solid var(--border); font-weight: 700;">Role Matrix</div><div style="display: grid; gap: 0;">${props.roles.map((role) => html`<div style="border-top: 1px solid var(--border); padding: 10px 12px;"><div style="display: flex; justify-content: space-between;"><strong>${role.name}</strong><span class="chip" style="font-size: 11px;">priority ${role.priority}</span></div>${role.description ? html`<div class="muted" style="font-size: 12px;">${role.description}</div>` : nothing}<div class="muted" style="font-size: 12px; margin-top: 4px;">model: <span class="mono">${role.model ?? "platform default"}</span></div><div style="margin-top: 6px; display: flex; gap: 6px; flex-wrap: wrap;">${role.skills.map((skill) => html`<span class="chip" style="font-size: 11px;">${skill}</span>`)}</div></div>`)}</div></div>
  `;
}

function renderUnitsPanel(props: WorkforceUiProps) {
  return html`
    <div class="card" style="margin-bottom: 10px;" data-unit-form>
      <div class="card-title">Add Unit</div>
      <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr auto; gap: 8px;">
        <input data-unit-id placeholder="unitId" />
        <input data-unit-name placeholder="name" />
        <select data-unit-parent>
          <option value="">root</option>
          ${props.units.map((unit) => html`<option value=${unit.unitId}>${unit.name}</option>`)}
        </select>
        <input data-unit-supervisor placeholder="supervisor employeeId" />
        <select data-unit-type>
          <option value="division">division</option>
          <option value="department">department</option>
          <option value="team">team</option>
          <option value="project">project</option>
        </select>
        <input data-unit-tags placeholder="tags (comma)" />
        <button class="btn primary" ?disabled=${props.saving} @click=${(ev: Event) => {
          const root = (ev.currentTarget as HTMLElement).closest("[data-unit-form]");
          const unitId = getInput(root, "[data-unit-id]");
          const name = getInput(root, "[data-unit-name]");
          const parentRaw = getInput(root, "[data-unit-parent]");
          const supervisorId = getInput(root, "[data-unit-supervisor]");
          const type = getInput(root, "[data-unit-type]") as
            | "division"
            | "department"
            | "team"
            | "project";
          const tags = splitCsv(getInput(root, "[data-unit-tags]"));
          if (!unitId || !name || !supervisorId || !type) return;
          props.onAddUnit({
            unitId,
            name,
            parentUnitId: parentRaw || null,
            supervisorId,
            type,
            ...(tags.length > 0 ? { tags } : {}),
          });
        }}>Add</button>
      </div>
    </div>
    <div class="card"><div class="card-title">Hierarchy Mapping</div><div class="muted" style="margin-bottom: 8px;">Map ATS org units into Prism Workflow to drive escalation and replacement analysis.</div>${renderHierarchyTree(props.units, props.agents)}</div>
  `;
}

function renderBindingsPanel(props: WorkforceUiProps) {
  return html`
    <div class="card" style="margin-bottom: 10px;" data-binding-form>
      <div class="card-title">Add Binding</div>
      <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr 1fr 0.8fr auto auto; gap: 8px;">
        <input data-binding-id placeholder="bindingId" />
        <select data-binding-agent>
          <option value="">agent</option>
          ${props.agents.map((agent) => html`<option value=${agent.agentId}>${agent.name}</option>`)}
        </select>
        <input data-binding-channels placeholder="channels (comma)" />
        <input data-binding-skills placeholder="requiredSkills (comma)" />
        <input data-binding-roles placeholder="roles (comma)" />
        <input data-binding-priority type="number" placeholder="priority" value="10" />
        <label class="muted" style="display: inline-flex; align-items: center; gap: 6px; font-size: 12px;"><input data-binding-fallback type="checkbox" />fallback</label>
        <button class="btn primary" ?disabled=${props.saving} @click=${(ev: Event) => {
          const root = (ev.currentTarget as HTMLElement).closest("[data-binding-form]");
          const bindingId = getInput(root, "[data-binding-id]");
          const agentId = getInput(root, "[data-binding-agent]");
          const channels = splitCsv(getInput(root, "[data-binding-channels]"));
          const requiredSkills = splitCsv(getInput(root, "[data-binding-skills]"));
          const roles = splitCsv(getInput(root, "[data-binding-roles]"));
          const priority = parseOptionalNumber(getInput(root, "[data-binding-priority]")) ?? 10;
          const fallback = Boolean(
            (root?.querySelector("[data-binding-fallback]") as HTMLInputElement | null)?.checked,
          );
          if (!bindingId || !agentId) return;
          props.onAddBinding({
            bindingId,
            agentId,
            ...(channels.length > 0 ? { channels } : {}),
            ...(requiredSkills.length > 0 ? { requiredSkills } : {}),
            ...(roles.length > 0 ? { roles } : {}),
            priority,
            fallback,
          });
        }}>Add</button>
      </div>
    </div>
    <div class="card" style="padding: 0; overflow: hidden;"><div style="padding: 12px 14px; border-bottom: 1px solid var(--border); font-weight: 700;">Routing Bindings</div>${
      props.bindings.length === 0
        ? html`
            <div class="muted" style="padding: 12px 14px">No bindings found in workflow config.</div>
          `
        : html`<table style="width: 100%; border-collapse: collapse;"><thead><tr style="text-align: left; font-size: 12px; color: var(--muted);"><th style="padding: 10px 12px;">Binding</th><th style="padding: 10px 12px;">Match</th><th style="padding: 10px 12px;">Target</th><th style="padding: 10px 12px;">Priority</th></tr></thead><tbody>${[
            ...props.bindings,
          ]
            .sort((a, b) => b.priority - a.priority)
            .map(
              (binding) =>
                html`<tr style="border-top: 1px solid var(--border);"><td style="padding: 10px 12px;"><div style="font-weight: 600;">${binding.bindingId}</div>${
                  binding.fallback
                    ? html`
                        <span class="chip-warn" style="font-size: 11px">fallback</span>
                      `
                    : nothing
                }</td><td style="padding: 10px 12px; font-size: 12px;">channels ${formatArray(binding.match.channels)}<br />roles ${formatArray(binding.match.roles)}<br />skills ${formatArray(binding.match.requiredSkills)}</td><td style="padding: 10px 12px;"><button class="chip" style="cursor: pointer;" @click=${() => props.onSelectAgent(binding.agentId)}>${binding.agentId}</button></td><td style="padding: 10px 12px;">${binding.priority}</td></tr>`,
            )} </tbody></table>`
    }</div>
  `;
}

function renderHierarchyTree(
  units: WorkforceUnitRow[],
  agents: WorkforceAgentRow[],
): TemplateResult {
  if (units.length === 0) {
    return html`
      <div class="muted">No units configured.</div>
    `;
  }
  const childrenByParent = new Map<string | null, WorkforceUnitRow[]>();
  for (const unit of units) {
    const current = childrenByParent.get(unit.parentUnitId) ?? [];
    current.push(unit);
    childrenByParent.set(unit.parentUnitId, current);
  }
  const resolveSupervisor = (unit: WorkforceUnitRow) => {
    const supervisor = agents.find((agent) => agent.employeeId === unit.supervisorId);
    return supervisor?.name ?? (unit.supervisorId || "unassigned");
  };
  const renderUnit = (unit: WorkforceUnitRow, depth: number): TemplateResult => {
    const childUnits = childrenByParent.get(unit.unitId) ?? [];
    const count = agents.filter((agent) => agent.unitId === unit.unitId).length;
    return html`<div style="margin-left: ${depth * 14}px; border-left: 1px dashed var(--border); padding-left: 8px; margin-top: 6px;"><div style="display: flex; align-items: center; gap: 8px;"><span class="chip" style="font-size: 10px;">${unit.type}</span><strong style="font-size: 13px;">${unit.name}</strong><span class="muted" style="font-size: 11px;">${count} agents</span></div><div class="muted" style="font-size: 11px; margin-top: 2px;">supervisor: ${resolveSupervisor(unit)}</div></div>${childUnits.map((child) => renderUnit(child, depth + 1))}`;
  };
  const roots = childrenByParent.get(null) ?? [];
  return html`${roots.map((root) => renderUnit(root, 0))}`;
}

function computeCriticalEmployees(props: WorkforceUiProps): CriticalEmployeeRow[] {
  const agentByEmployeeId = new Map(props.agents.map((agent) => [agent.employeeId, agent]));
  const roleCounts = countBy(
    props.agents.map((agent) => agent.roleId ?? agent.role ?? "unassigned"),
  );
  const unitCounts = countBy(props.agents.map((agent) => agent.unitId ?? "unassigned"));
  const supervisorSet = new Set(props.units.map((unit) => unit.supervisorId).filter(Boolean));

  const out: CriticalEmployeeRow[] = [];
  for (const employee of props.employees) {
    const mapped = agentByEmployeeId.get(employee.employee.employeeId);
    if (!mapped) {
      continue;
    }
    const reasons: string[] = [];
    let score = 0;
    const role = mapped.roleId ?? mapped.role ?? "unassigned";
    const unit = mapped.unitId ?? "unassigned";

    if ((roleCounts.get(role) ?? 0) <= 1) {
      score += 4;
      reasons.push("single-role-coverage");
    }
    if ((unitCounts.get(unit) ?? 0) <= 1) {
      score += 3;
      reasons.push("single-unit-coverage");
    }
    if (supervisorSet.has(employee.employee.employeeId)) {
      score += 5;
      reasons.push("org-supervisor");
    }
    if ((mapped.skills?.length ?? 0) >= 4) {
      score += 2;
      reasons.push("high-skill-density");
    }
    if (mapped.status !== "active") {
      score -= 4;
      reasons.push("inactive");
    }

    if (score > 0) {
      out.push({
        employeeId: employee.employee.employeeId,
        name: employee.employee.name,
        role,
        unit,
        score,
        reasons,
      });
    }
  }
  return out.sort((a, b) => b.score - a.score);
}

function countBy(values: string[]) {
  const counts = new Map<string, number>();
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return counts;
}

function formatArray(values?: string[]) {
  if (!values || values.length === 0) {
    return "-";
  }
  return values.join(", ");
}

function splitCsv(raw: string): string[] {
  return raw
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

function parseOptionalNumber(raw: string): number | null {
  if (!raw.trim()) {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function getInput(root: Element | null, selector: string): string {
  const node = root?.querySelector<HTMLInputElement | HTMLSelectElement>(selector);
  return (node?.value ?? "").trim();
}

function labelForView(view: WorkforceView) {
  switch (view) {
    case "overview":
      return "Overview";
    case "agents":
      return "Agents";
    case "roles":
      return "Roles";
    case "units":
      return "Hierarchy";
    case "bindings":
      return "Bindings";
    case "routing":
      return "Routing";
    case "analytics":
      return "Analytics";
    case "fleet":
      return "Fleet";
    default:
      return view;
  }
}
